import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Button, ButtonBase, Divider, IconButton, InputAdornment, MenuItem,
    Paper, Slider, Stack, Switch, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import AddIcon                   from '@mui/icons-material/Add';
import CloseIcon                 from '@mui/icons-material/Close';
import DeleteOutlineIcon         from '@mui/icons-material/DeleteOutline';
import ContentCopyOutlinedIcon   from '@mui/icons-material/ContentCopyOutlined';
import FormatBoldIcon            from '@mui/icons-material/FormatBold';
import FormatItalicIcon          from '@mui/icons-material/FormatItalic';
import FormatUnderlinedIcon      from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon        from '@mui/icons-material/StrikethroughS';

const LANGS = [
    { code: 'nl', short: 'NL', label: 'Nederlands' },
    { code: 'fr', short: 'FR', label: 'Français'   },
    { code: 'en', short: 'EN', label: 'English'    },
];

// Static option pools. Kept inline because the modal is the only consumer
// and the values map 1:1 to storefront identifiers.
const HEIGHT_UNITS    = [{ v: 'px', l: 'Pixels (px)' }, { v: 'percent', l: 'Percent (%)' }, { v: 'vh', l: 'Viewport height (vh)' }];
const FIT_OPTIONS     = [
    { v: 'scale', l: 'Scale to fit (preserve ratio)' },
    { v: 'cover', l: 'Cover (fill, may crop)'        },
    { v: 'contain', l: 'Contain (whole image)'        },
];
const HOVER_EFFECTS   = [
    { v: 'none',     l: 'None'      },
    { v: 'zoom',     l: 'Zoom in'   },
    { v: 'darken',   l: 'Darken'    },
    { v: 'fade',     l: 'Fade'      },
    { v: 'slideUp',  l: 'Slide up'  },
];
const TRANSITION_STYLES = [
    { v: 'slideRtoL', l: 'Slide in/out (right to left)' },
    { v: 'slideLtoR', l: 'Slide in/out (left to right)' },
    { v: 'fade',      l: 'Crossfade'                    },
    { v: 'zoom',      l: 'Zoom'                         },
];
const POSITIONS = [
    { v: 'top-left',     l: 'Top left'      },
    { v: 'top-center',   l: 'Top center'    },
    { v: 'top-right',    l: 'Top right'     },
    { v: 'center',       l: 'Center'        },
    { v: 'bottom-left',  l: 'Bottom left'   },
    { v: 'bottom-center',l: 'Bottom center' },
    { v: 'bottom-right', l: 'Bottom right'  },
];

const blankSlide = (i = 1) => ({
    id:       `s_${Date.now()}_${i}`,
    order:    i,
    active:   true,
    desktopImage: null,
    mobileImage:  null,
    linkUrl:  '',
    hoverDesktop: '',
    hoverMobile:  '',
});

const CarouselConfigModal = ({ theme, t }) => {
    const tr = (key, fallback) => (typeof t === 'function' ? t(key) : fallback);

    const [tab, setTab]         = useState(0);   // 0=Slides, 1=Settings
    const [langTab, setLangTab] = useState(0);

    const [config, setConfig] = useState({
        slides: {
            nl: [
                { ...blankSlide(1), hoverDesktop: 'desk 2', hoverMobile: 'mobiel 2', linkUrl: 'https://testshop.fashionretail.be/nl/kleding?page=1' },
                { ...blankSlide(2), hoverDesktop: 'test 1', hoverMobile: 'test 2',   linkUrl: 'https://testshop.fashionretail.be/nl/kleding?page=1' },
            ],
            en: [],
            fr: [],
        },
        settings: {
            display: {
                fullWidth:   false,
                maxHeight:   600,
                maxHeightUnit: 'px',
                spacingTop:  0,
                spacingBottom: 0,
                imageFit:    'scale',
            },
            hover: {
                effect:        'zoom',
                zoomLevel:     140,
                animationSpeed:0.3,
            },
            behavior: {
                showArrows:        true,
                arrowsOnHover:     true,
                arrowsBackground:  false,
                autoAdvance:       true,
                timePerSlide:      2,
                transition:        'slideRtoL',
            },
            textOverlay: {
                desktop: { background: '#ffffff', textColor: '#000000', position: 'bottom-center', size: 12, width: 10, bold: false, italic: false, underline: false, strike: false },
                mobile:  { background: '#ffffff', textColor: '#000000', position: 'bottom-center', size: 12, width: 10, bold: false, italic: false, underline: false, strike: false },
                onlyOnHover:        true,
                alwaysVisible:      false,
                useDesktopOnMobile: false,
            },
        },
    });

    const lang = LANGS[langTab].code;

    // ────────── Slides handlers ──────────
    const slidesForLang = config.slides[lang] || [];
    const setSlides = (newList) => setConfig(p => ({ ...p, slides: { ...p.slides, [lang]: newList } }));
    const updateSlide = (id, patch) => setSlides(slidesForLang.map(s => s.id === id ? { ...s, ...patch } : s));
    const removeSlide = (id) => setSlides(slidesForLang.filter(s => s.id !== id));
    const addSlide    = () => setSlides([...slidesForLang, blankSlide(slidesForLang.length + 1)]);
    const copyFrom    = (fromLang) => {
        if (fromLang === lang) return;
        const src = (config.slides[fromLang] || []).map(s => ({ ...s, id: `s_${Date.now()}_${Math.random()}` }));
        setSlides(src);
    };

    // ────────── Settings handlers ──────────
    const setSettings = (section) => (patch) => setConfig(p => ({
        ...p,
        settings: { ...p.settings, [section]: { ...(p.settings[section] || {}), ...patch } },
    }));
    const setOverlay = (device) => (patch) => setConfig(p => ({
        ...p,
        settings: {
            ...p.settings,
            textOverlay: {
                ...p.settings.textOverlay,
                [device]: { ...(p.settings.textOverlay[device] || {}), ...patch },
            },
        },
    }));
    const setOverlayFlag = (key, val) => setConfig(p => ({
        ...p,
        settings: {
            ...p.settings,
            textOverlay: { ...p.settings.textOverlay, [key]: val },
        },
    }));

    const activeCount = slidesForLang.filter(s => s.active).length;

    return (
        <Box>
            {/* Top bar — language pills + COPY FROM popover trigger */}
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2,
                mb: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider',
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                    {LANGS.map((l, idx) => (
                        <ButtonBase
                            key={l.code}
                            onClick={() => setLangTab(idx)}
                            sx={{
                                px: 1.5, py: 0.5, borderRadius: 0.75,
                                fontWeight: 600, fontSize: '0.85rem',
                                color:           langTab === idx ? 'primary.main' : 'text.secondary',
                                backgroundColor: langTab === idx ? 'rgba(25,118,210,0.08)' : 'transparent',
                                '&:hover':       { backgroundColor: 'rgba(25,118,210,0.06)' },
                            }}
                        >
                            <Box component="span" sx={{ fontSize: '0.7rem', color: 'text.disabled', mr: 0.5 }}>{l.short}</Box>
                            {l.label}
                        </ButtonBase>
                    ))}
                </Box>
                <CopyFromMenu langs={LANGS} currentLang={lang} onCopy={copyFrom} tr={tr} />
            </Box>

            {/* Tab switcher — Slides | Settings */}
            <Tabs
                value={tab}
                onChange={(_e, v) => setTab(v)}
                centered
                sx={{
                    minHeight: 40,
                    borderBottom: '1px solid', borderColor: 'divider',
                    mb: 2,
                    '& .MuiTab-root': { minHeight: 40, textTransform: 'uppercase', fontWeight: 700, letterSpacing: 0.5, fontSize: '0.85rem' },
                }}
            >
                <Tab label={tr('cfg_carousel_tab_slides',   'Slides')} />
                <Tab label={tr('cfg_carousel_tab_settings', 'Settings')} />
            </Tabs>

            {tab === 0 && (
                <SlidesPanel
                    slides={slidesForLang}
                    activeCount={activeCount}
                    onUpdate={updateSlide}
                    onRemove={removeSlide}
                    onAdd={addSlide}
                    tr={tr}
                />
            )}
            {tab === 1 && (
                <SettingsPanel
                    settings={config.settings}
                    setSection={setSettings}
                    setOverlay={setOverlay}
                    setOverlayFlag={setOverlayFlag}
                    slides={slidesForLang}
                    tr={tr}
                    theme={theme}
                />
            )}
        </Box>
    );
};
CarouselConfigModal.propTypes = { theme: PropTypes.string, t: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// COPY FROM popover button — picks a source language and triggers a copy.
// ─────────────────────────────────────────────────────────────────────────
const CopyFromMenu = ({ langs, currentLang, onCopy, tr }) => {
    const [anchor, setAnchor] = useState(null);
    return (
        <>
            <Button
                size="small"
                variant="text"
                startIcon={<ContentCopyOutlinedIcon fontSize="small" />}
                onClick={(e) => setAnchor(e.currentTarget)}
                sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: '0.75rem', letterSpacing: 0.5 }}
            >
                {tr('cfg_carousel_copy_from', 'Copy from')} …
            </Button>
            {anchor && (
                <Box
                    onPointerDown={(e) => e.stopPropagation()}
                    sx={{
                        position: 'absolute', mt: 0.5,
                        right: 24, top: 56,
                        backgroundColor: '#fff',
                        border: '1px solid', borderColor: 'divider',
                        borderRadius: 1, boxShadow: 2,
                        p: 0.5, zIndex: 10,
                    }}
                >
                    {langs.filter(l => l.code !== currentLang).map(l => (
                        <ButtonBase
                            key={l.code}
                            onClick={() => { onCopy(l.code); setAnchor(null); }}
                            sx={{ display: 'block', width: '100%', textAlign: 'left', px: 1.25, py: 0.75, borderRadius: 0.75, '&:hover': { backgroundColor: 'rgba(25,118,210,0.06)' } }}
                        >
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                <Box component="span" sx={{ color: 'text.disabled', mr: 0.5, fontSize: '0.7rem' }}>{l.short}</Box>
                                {l.label}
                            </Typography>
                        </ButtonBase>
                    ))}
                    <Divider sx={{ my: 0.5 }} />
                    <ButtonBase
                        onClick={() => setAnchor(null)}
                        sx={{ display: 'block', width: '100%', textAlign: 'left', px: 1.25, py: 0.5, fontSize: '0.75rem', color: 'text.secondary' }}
                    >
                        {tr('cancel', 'Cancel')}
                    </ButtonBase>
                </Box>
            )}
        </>
    );
};
CopyFromMenu.propTypes = { langs: PropTypes.array, currentLang: PropTypes.string, onCopy: PropTypes.func, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// SLIDES panel
// ─────────────────────────────────────────────────────────────────────────
const SlidesPanel = ({ slides, activeCount, onUpdate, onRemove, onAdd, tr }) => (
    <Box>
        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 1.5 }}>
            {tr('cfg_carousel_slides_summary', 'Carousel slides')} · {activeCount} {tr('cfg_carousel_active', 'active')}
        </Typography>
        <Stack spacing={2}>
            {slides.map(slide => (
                <SlideCard key={slide.id} slide={slide} onUpdate={onUpdate} onRemove={onRemove} tr={tr} />
            ))}
            <ButtonBase
                onClick={onAdd}
                sx={{
                    py: 1.5, borderRadius: 1.5,
                    border: '1.5px dashed', borderColor: 'divider',
                    color: 'text.secondary',
                    gap: 0.75,
                    '&:hover': { borderColor: 'primary.main', color: 'primary.main', backgroundColor: 'rgba(25,118,210,0.04)' },
                }}
            >
                <AddIcon fontSize="small" />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {tr('cfg_carousel_add_slide', 'Add new slide')}
                </Typography>
            </ButtonBase>
        </Stack>
    </Box>
);
SlidesPanel.propTypes = { slides: PropTypes.array, activeCount: PropTypes.number, onUpdate: PropTypes.func, onRemove: PropTypes.func, onAdd: PropTypes.func, tr: PropTypes.func };

const SlideCard = ({ slide, onUpdate, onRemove, tr }) => {
    return (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, backgroundColor: 'rgba(0,0,0,0.02)' }}>
            {/* Header — Order [number] · [toggle] Active                       [Delete slide]
                Inline label + small number input keeps the row at the same
                height as the toggle; floating-label TextField looked off. */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {tr('cfg_carousel_slide_order', 'Order')}
                        </Typography>
                        <TextField
                            size="small" type="number"
                            value={slide.order}
                            onChange={(e) => onUpdate(slide.id, { order: Math.max(1, parseInt(e.target.value) || 1) })}
                            sx={{ width: 72, '& .MuiInputBase-input': { textAlign: 'center', py: 0.5 } }}
                            inputProps={{ min: 1 }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Switch
                            color="success" size="small"
                            checked={!!slide.active}
                            onChange={(e) => onUpdate(slide.id, { active: e.target.checked })}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {tr('cfg_carousel_slide_active', 'Active')}
                        </Typography>
                    </Box>
                </Box>
                <Button
                    size="small" color="error" variant="outlined"
                    startIcon={<DeleteOutlineIcon fontSize="small" />}
                    onClick={() => onRemove(slide.id)}
                    sx={{ textTransform: 'none', borderRadius: 1.5 }}
                >
                    {tr('cfg_carousel_slide_delete', 'Delete slide')}
                </Button>
            </Box>

            {/* Image slots — Desktop + Mobile.
                Both slots use the same aspect (16/9) so the two columns line
                up in the admin. The actual stored image can be portrait if
                the user uploads one — only the preview/slot is consistent. */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
                <ImageSlot
                    label={tr('cfg_carousel_slide_desktop_img', 'DESKTOP IMAGE')}
                    value={slide.desktopImage}
                    onChange={(v) => onUpdate(slide.id, { desktopImage: v })}
                    tr={tr}
                />
                <ImageSlot
                    label={tr('cfg_carousel_slide_mobile_img', 'MOBILE IMAGE')}
                    value={slide.mobileImage}
                    onChange={(v) => onUpdate(slide.id, { mobileImage: v })}
                    tr={tr}
                />
            </Box>

            {/* Link URL */}
            <Box sx={{ mb: 2 }}>
                <UpperLabel>{tr('cfg_carousel_slide_link', 'LINK URL')}</UpperLabel>
                <TextField
                    size="small" fullWidth
                    value={slide.linkUrl}
                    onChange={(e) => onUpdate(slide.id, { linkUrl: e.target.value })}
                    placeholder="https://"
                    sx={{ backgroundColor: '#fff' }}
                />
            </Box>

            {/* Hover text — desktop + mobile */}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <Box>
                    <UpperLabel>{tr('cfg_carousel_slide_hover_desktop', 'HOVER TEXT DESKTOP')}</UpperLabel>
                    <TextField
                        size="small" fullWidth
                        value={slide.hoverDesktop}
                        onChange={(e) => onUpdate(slide.id, { hoverDesktop: e.target.value })}
                        sx={{ backgroundColor: '#fff' }}
                    />
                </Box>
                <Box>
                    <UpperLabel>{tr('cfg_carousel_slide_hover_mobile', 'HOVER TEXT MOBILE')}</UpperLabel>
                    <TextField
                        size="small" fullWidth
                        value={slide.hoverMobile}
                        onChange={(e) => onUpdate(slide.id, { hoverMobile: e.target.value })}
                        sx={{ backgroundColor: '#fff' }}
                    />
                </Box>
            </Box>
        </Paper>
    );
};
SlideCard.propTypes = { slide: PropTypes.object, onUpdate: PropTypes.func, onRemove: PropTypes.func, tr: PropTypes.func };

// Image upload slot — one clickable area in both states.
//   • Empty → dashed zone with "+ Upload" centered.
//   • Filled → image preview. Clicking anywhere on the image triggers the
//     file picker (replace). A small × in the corner removes the image.
//     A "Replace" hover-overlay appears on mouseover as a visual cue.
const ImageSlot = ({ label, value, onChange, tr }) => {
    const onFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const url = URL.createObjectURL(file);
        onChange({ name: file.name, url });
    };
    const aspect = '16/9';
    return (
        <Box>
            <UpperLabel>{label}</UpperLabel>
            {value ? (
                <Box sx={{
                    position: 'relative',
                    aspectRatio: aspect,
                    borderRadius: 1.5,
                    border: '1px solid', borderColor: 'divider',
                    backgroundColor: '#fff',
                    overflow: 'hidden',
                    '&:hover .replace-overlay': { opacity: 1 },
                }}>
                    {/* Click-to-replace label wraps the image */}
                    <Box component="label" sx={{ display: 'block', cursor: 'pointer', width: '100%', height: '100%' }}>
                        <Box component="img" src={value.url} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                        <Box className="replace-overlay" sx={{
                            position: 'absolute', inset: 0,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75,
                            backgroundColor: 'rgba(0,0,0,0.45)',
                            color: '#fff',
                            opacity: 0, transition: 'opacity 0.15s',
                        }}>
                            <AddIcon fontSize="small" />
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {tr('cfg_carousel_replace', 'Replace')}
                            </Typography>
                        </Box>
                        <input type="file" accept="image/*" onChange={onFile} style={{ display: 'none' }} />
                    </Box>
                    {/* × remove sits ABOVE the click-to-replace label so it
                        catches the click first. stopPropagation prevents the
                        file picker from also opening. */}
                    <IconButton
                        size="small"
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onChange(null); }}
                        sx={{
                            position: 'absolute', top: 6, right: 6,
                            width: 26, height: 26,
                            backgroundColor: 'rgba(255,255,255,0.92)',
                            boxShadow: '0 1px 3px rgba(0,0,0,0.25)',
                            zIndex: 1,
                            '&:hover': { backgroundColor: '#fff' },
                        }}
                    >
                        <CloseIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                </Box>
            ) : (
                <ButtonBase
                    component="label"
                    sx={{
                        width: '100%',
                        aspectRatio: aspect,
                        borderRadius: 1.5,
                        border: '1.5px dashed', borderColor: 'divider',
                        backgroundColor: 'rgba(0,0,0,0.02)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        gap: 0.5,
                        color: 'text.secondary',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: 'primary.main', backgroundColor: 'rgba(25,118,210,0.04)', color: 'primary.main' },
                    }}
                >
                    <AddIcon fontSize="small" />
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{tr('cfg_carousel_upload', 'Upload')}</Typography>
                    <input type="file" accept="image/*" onChange={onFile} style={{ display: 'none' }} />
                </ButtonBase>
            )}
        </Box>
    );
};
ImageSlot.propTypes = { label: PropTypes.string, value: PropTypes.object, onChange: PropTypes.func, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// SETTINGS panel
// ─────────────────────────────────────────────────────────────────────────
const SettingsPanel = ({ settings, setSection, setOverlay, setOverlayFlag, slides, tr, theme }) => {
    const setDisplay  = setSection('display');
    const setHover    = setSection('hover');
    const setBehavior = setSection('behavior');

    // Find the first active slide with a desktop image to drive the preview.
    const previewSrc = slides.find(s => s.active && s.desktopImage)?.desktopImage?.url;

    return (
        <Stack spacing={2}>
            {/* Live preview area */}
            <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
                <Box sx={{
                    aspectRatio: '16 / 6',
                    backgroundColor: 'rgba(0,0,0,0.03)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: 'text.disabled',
                    backgroundImage: previewSrc ? `url(${previewSrc})` : undefined,
                    backgroundSize: settings.display.imageFit === 'scale' ? 'contain' : (settings.display.imageFit === 'cover' ? 'cover' : 'contain'),
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    fontSize: '0.85rem',
                    letterSpacing: 1,
                }}>
                    {!previewSrc && tr('cfg_carousel_preview_placeholder', 'PREVIEW')}
                </Box>
            </Paper>

            {/* Display */}
            <Card title={tr('cfg_carousel_display_title', 'Display')} desc={tr('cfg_carousel_display_desc', 'How the carousel sits on the page.')}>
                <SettingRow
                    label={tr('cfg_carousel_full_width', 'Full-width gallery')}
                    help={tr('cfg_carousel_full_width_help', 'Stretch to screen edges')}
                    control={<Switch color="success" size="small" checked={!!settings.display.fullWidth} onChange={(e) => setDisplay({ fullWidth: e.target.checked })} />}
                />
                <SettingRow
                    label={tr('cfg_carousel_max_height', 'Maximum height')}
                    control={
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <TextField size="small" type="number"
                                value={settings.display.maxHeight}
                                onChange={(e) => setDisplay({ maxHeight: Math.max(0, parseInt(e.target.value) || 0) })}
                                sx={{ width: 110 }}
                            />
                            <TextField size="small" select
                                value={settings.display.maxHeightUnit}
                                onChange={(e) => setDisplay({ maxHeightUnit: e.target.value })}
                                sx={{ width: 130 }}
                            >
                                {HEIGHT_UNITS.map(o => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
                            </TextField>
                        </Box>
                    }
                />
                <SettingRow
                    label={tr('cfg_carousel_extra_spacing', 'Extra spacing')}
                    control={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{tr('cfg_carousel_top', 'Top')}</Typography>
                            <TextField size="small" type="number"
                                value={settings.display.spacingTop}
                                onChange={(e) => setDisplay({ spacingTop: Math.max(0, parseInt(e.target.value) || 0) })}
                                InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                                sx={{ width: 100 }}
                            />
                            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 1 }}>{tr('cfg_carousel_bottom', 'Bottom')}</Typography>
                            <TextField size="small" type="number"
                                value={settings.display.spacingBottom}
                                onChange={(e) => setDisplay({ spacingBottom: Math.max(0, parseInt(e.target.value) || 0) })}
                                InputProps={{ endAdornment: <InputAdornment position="end">px</InputAdornment> }}
                                sx={{ width: 100 }}
                            />
                        </Box>
                    }
                />
                <SettingRow
                    label={tr('cfg_carousel_image_fit', 'Image fit')}
                    control={
                        <TextField size="small" select
                            value={settings.display.imageFit}
                            onChange={(e) => setDisplay({ imageFit: e.target.value })}
                            sx={{ minWidth: 240 }}
                        >
                            {FIT_OPTIONS.map(o => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
                        </TextField>
                    }
                />
            </Card>

            {/* Hover effect */}
            <Card title={tr('cfg_carousel_hover_title', 'Hover effect')} desc={tr('cfg_carousel_hover_desc', 'What happens when someone hovers over a slide.')}>
                <SettingRow
                    label={tr('cfg_carousel_hover_effect', 'Effect')}
                    control={
                        <TextField size="small" select
                            value={settings.hover.effect}
                            onChange={(e) => setHover({ effect: e.target.value })}
                            sx={{ minWidth: 200 }}
                        >
                            {HOVER_EFFECTS.map(o => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
                        </TextField>
                    }
                />
                <SettingRow
                    label={tr('cfg_carousel_zoom_level', 'Zoom level')}
                    control={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: 280 }}>
                            <Slider
                                size="small"
                                value={settings.hover.zoomLevel}
                                min={100} max={200} step={5}
                                onChange={(_, v) => setHover({ zoomLevel: v })}
                                sx={{ flex: 1 }}
                            />
                            <Typography variant="body2" sx={{ minWidth: 48, textAlign: 'right', fontWeight: 600 }}>
                                {settings.hover.zoomLevel}%
                            </Typography>
                        </Box>
                    }
                />
                <SettingRow
                    label={tr('cfg_carousel_anim_speed', 'Animation speed')}
                    control={
                        <TextField size="small" type="number"
                            value={settings.hover.animationSpeed}
                            onChange={(e) => setHover({ animationSpeed: Math.max(0, parseFloat(e.target.value.replace(',', '.')) || 0) })}
                            InputProps={{ endAdornment: <InputAdornment position="end" sx={{ whiteSpace: 'nowrap', '& .MuiTypography-root': { whiteSpace: 'nowrap' } }}>{tr('cfg_carousel_seconds', 'seconds')}</InputAdornment> }}
                            inputProps={{ step: 0.1 }}
                            sx={{ width: 160 }}
                        />
                    }
                />
            </Card>

            {/* Slide behavior */}
            <Card title={tr('cfg_carousel_behavior_title', 'Slide behavior')} desc={tr('cfg_carousel_behavior_desc', 'Navigation, autoplay, and transitions between slides.')}>
                <Stack spacing={1.25} divider={<Divider />}>
                    <SettingRow
                        label={tr('cfg_carousel_show_arrows', 'Show navigation arrows')}
                        help={tr('cfg_carousel_show_arrows_help', 'Let visitors click left/right to change slides')}
                        labelLeft
                        control={<Switch color="success" size="small" checked={!!settings.behavior.showArrows} onChange={(e) => setBehavior({ showArrows: e.target.checked })} />}
                    />
                    <Box sx={{ pl: 4, opacity: settings.behavior.showArrows ? 1 : 0.5, pointerEvents: settings.behavior.showArrows ? 'auto' : 'none' }}>
                        <Stack spacing={0.5}>
                            <SettingRow
                                label={tr('cfg_carousel_arrows_on_hover', 'Only show arrows on hover')}
                                labelLeft
                                control={<Switch color="success" size="small" checked={!!settings.behavior.arrowsOnHover} onChange={(e) => setBehavior({ arrowsOnHover: e.target.checked })} />}
                            />
                            <SettingRow
                                label={tr('cfg_carousel_arrows_bg', 'Add background behind arrows')}
                                labelLeft
                                control={<Switch color="success" size="small" checked={!!settings.behavior.arrowsBackground} onChange={(e) => setBehavior({ arrowsBackground: e.target.checked })} />}
                            />
                        </Stack>
                    </Box>
                    <SettingRow
                        label={tr('cfg_carousel_auto_advance', 'Auto-advance slides')}
                        help={tr('cfg_carousel_auto_advance_help', 'Move to the next slide automatically')}
                        labelLeft
                        control={<Switch color="success" size="small" checked={!!settings.behavior.autoAdvance} onChange={(e) => setBehavior({ autoAdvance: e.target.checked })} />}
                    />
                    <Box sx={{ opacity: settings.behavior.autoAdvance ? 1 : 0.5, pointerEvents: settings.behavior.autoAdvance ? 'auto' : 'none' }}>
                        <SettingRow
                            label={tr('cfg_carousel_time_per_slide', 'Time on each slide')}
                            control={
                                <TextField size="small" type="number"
                                    value={settings.behavior.timePerSlide}
                                    onChange={(e) => setBehavior({ timePerSlide: Math.max(0, parseFloat(e.target.value.replace(',', '.')) || 0) })}
                                    InputProps={{ endAdornment: <InputAdornment position="end" sx={{ whiteSpace: 'nowrap', '& .MuiTypography-root': { whiteSpace: 'nowrap' } }}>{tr('cfg_carousel_seconds', 'seconds')}</InputAdornment> }}
                                    inputProps={{ step: 0.5 }}
                                    sx={{ width: 160 }}
                                />
                            }
                        />
                    </Box>
                    <SettingRow
                        label={tr('cfg_carousel_transition', 'Transition style')}
                        control={
                            <TextField size="small" select
                                value={settings.behavior.transition}
                                onChange={(e) => setBehavior({ transition: e.target.value })}
                                sx={{ minWidth: 280 }}
                            >
                                {TRANSITION_STYLES.map(o => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
                            </TextField>
                        }
                    />
                </Stack>
            </Card>

            {/* Text overlay */}
            <Card title={tr('cfg_carousel_overlay_title', 'Text overlay')} desc={tr('cfg_carousel_overlay_desc', 'Style the hover text shown on each slide.')}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                    <OverlayDeviceCard label={tr('cfg_carousel_overlay_desktop', 'Desktop')} data={settings.textOverlay.desktop} onChange={setOverlay('desktop')} tr={tr} />
                    <OverlayDeviceCard label={tr('cfg_carousel_overlay_mobile',  'Mobile')}  data={settings.textOverlay.mobile}  onChange={setOverlay('mobile')}  tr={tr} />
                </Box>

                <Stack spacing={1.25} divider={<Divider />} sx={{ mt: 1.5 }}>
                    <SettingRow
                        label={tr('cfg_carousel_overlay_only_hover', 'Only show overlay on hover')}
                        help={tr('cfg_carousel_desktop_only', 'Desktop only')}
                        labelLeft
                        control={<Switch color="success" size="small" checked={!!settings.textOverlay.onlyOnHover} onChange={(e) => setOverlayFlag('onlyOnHover', e.target.checked)} />}
                    />
                    <SettingRow
                        label={tr('cfg_carousel_overlay_always', 'Keep text always visible')}
                        help={tr('cfg_carousel_desktop_only', 'Desktop only')}
                        labelLeft
                        control={<Switch color="success" size="small" checked={!!settings.textOverlay.alwaysVisible} onChange={(e) => setOverlayFlag('alwaysVisible', e.target.checked)} />}
                    />
                    <SettingRow
                        label={tr('cfg_carousel_overlay_fallback', 'Use desktop text on mobile if mobile is empty')}
                        labelLeft
                        control={<Switch color="success" size="small" checked={!!settings.textOverlay.useDesktopOnMobile} onChange={(e) => setOverlayFlag('useDesktopOnMobile', e.target.checked)} />}
                    />
                </Stack>
            </Card>
        </Stack>
    );
};
SettingsPanel.propTypes = { settings: PropTypes.object, setSection: PropTypes.func, setOverlay: PropTypes.func, setOverlayFlag: PropTypes.func, slides: PropTypes.array, tr: PropTypes.func, theme: PropTypes.string };

const OverlayDeviceCard = ({ label, data = {}, onChange, tr }) => (
    <Box sx={{ p: 1.5, borderRadius: 1.5, backgroundColor: 'rgba(0,0,0,0.03)', border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>{label}</Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
            <Box>
                <UpperLabel>{tr('cfg_carousel_overlay_bg', 'BACKGROUND')}</UpperLabel>
                <ColorSwatch value={data.background} onChange={(v) => onChange({ background: v })} />
            </Box>
            <Box>
                <UpperLabel>{tr('cfg_carousel_overlay_text_color', 'TEXT COLOR')}</UpperLabel>
                <ColorSwatch value={data.textColor} onChange={(v) => onChange({ textColor: v })} />
            </Box>
        </Box>
        <Box sx={{ mb: 1.5 }}>
            <UpperLabel>{tr('cfg_carousel_overlay_position', 'POSITION')}</UpperLabel>
            <TextField select size="small" fullWidth
                value={data.position || 'bottom-center'}
                onChange={(e) => onChange({ position: e.target.value })}
                sx={{ backgroundColor: '#fff' }}
            >
                {POSITIONS.map(o => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
            </TextField>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 1.5 }}>
            <Box>
                <UpperLabel>{tr('cfg_carousel_overlay_size', 'SIZE')}</UpperLabel>
                <TextField size="small" type="number" fullWidth
                    value={data.size ?? 12}
                    onChange={(e) => onChange({ size: Math.max(8, parseInt(e.target.value) || 0) })}
                    sx={{ backgroundColor: '#fff' }}
                />
            </Box>
            <Box>
                <UpperLabel>{tr('cfg_carousel_overlay_width', 'WIDTH')}</UpperLabel>
                <TextField size="small" type="number" fullWidth
                    value={data.width ?? 10}
                    onChange={(e) => onChange({ width: Math.max(0, parseInt(e.target.value) || 0) })}
                    sx={{ backgroundColor: '#fff' }}
                />
            </Box>
        </Box>
        <Box>
            <UpperLabel>{tr('cfg_carousel_overlay_style', 'STYLE')}</UpperLabel>
            <Box sx={{ display: 'flex', gap: 0.5 }}>
                <StyleToggle active={!!data.bold}      onClick={() => onChange({ bold: !data.bold })}>          <FormatBoldIcon       fontSize="small" /></StyleToggle>
                <StyleToggle active={!!data.italic}    onClick={() => onChange({ italic: !data.italic })}>      <FormatItalicIcon     fontSize="small" /></StyleToggle>
                <StyleToggle active={!!data.underline} onClick={() => onChange({ underline: !data.underline })}><FormatUnderlinedIcon fontSize="small" /></StyleToggle>
                <StyleToggle active={!!data.strike}    onClick={() => onChange({ strike: !data.strike })}>      <StrikethroughSIcon   fontSize="small" /></StyleToggle>
            </Box>
        </Box>
    </Box>
);
OverlayDeviceCard.propTypes = { label: PropTypes.string, data: PropTypes.object, onChange: PropTypes.func, tr: PropTypes.func };

// ─────────────────────────────────────────────────────────────────────────
// Local helpers
// ─────────────────────────────────────────────────────────────────────────

const Card = ({ title, desc, children }) => (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Box sx={{ mb: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
            {desc && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>{desc}</Typography>}
        </Box>
        <Stack spacing={1}>
            {children}
        </Stack>
    </Paper>
);
Card.propTypes = { title: PropTypes.node, desc: PropTypes.node, children: PropTypes.node };

const SettingRow = ({ label, help, control, labelLeft = false }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
        {labelLeft ? (
            <>
                <Box sx={{ flexShrink: 0 }}>{control}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
                    {help && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>{help}</Typography>}
                </Box>
            </>
        ) : (
            <>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{label}</Typography>
                    {help && <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.4 }}>{help}</Typography>}
                </Box>
                <Box sx={{ flexShrink: 0 }}>{control}</Box>
            </>
        )}
    </Box>
);
SettingRow.propTypes = { label: PropTypes.node, help: PropTypes.node, control: PropTypes.node, labelLeft: PropTypes.bool };

const UpperLabel = ({ children }) => (
    <Typography variant="caption" sx={{
        display: 'block', fontWeight: 700, color: 'text.secondary',
        letterSpacing: 0.5, fontSize: '0.7rem', mb: 0.5,
        textTransform: 'uppercase',
    }}>
        {children}
    </Typography>
);
UpperLabel.propTypes = { children: PropTypes.node };

const ColorSwatch = ({ value, onChange }) => (
    <Box component="label" sx={{
        position: 'relative', display: 'block',
        width: 36, height: 36, borderRadius: 1,
        border: '1px solid rgba(0,0,0,0.18)',
        backgroundColor: value || '#ffffff',
        cursor: 'pointer',
    }}>
        <input type="color" value={value || '#ffffff'} onChange={(e) => onChange(e.target.value)}
            style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }} />
    </Box>
);
ColorSwatch.propTypes = { value: PropTypes.string, onChange: PropTypes.func };

const StyleToggle = ({ active, onClick, children }) => (
    <IconButton
        size="small"
        onClick={onClick}
        sx={{
            border: '1px solid',
            borderColor: active ? 'primary.main' : 'divider',
            borderRadius: 1,
            color: active ? 'primary.main' : 'text.primary',
            backgroundColor: active ? 'rgba(25,118,210,0.08)' : 'transparent',
            width: 34, height: 34,
        }}
    >
        {children}
    </IconButton>
);
StyleToggle.propTypes = { active: PropTypes.bool, onClick: PropTypes.func, children: PropTypes.node };

export default CarouselConfigModal;
